import React, { useMemo } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import clsx from 'clsx'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import { SaveComponentButtonItemProps, SaveComponentButtonType, SaveComponentProps } from './_props'
import useStyles from './_styles'
import { getSaveComponentButtons } from './_utils'
import { displaySnackbar } from '../../../actions/main'
import { useAppDispatch } from '../../../store'
import Prompt from '../Prompt'

const SaveComponent: React.FC<SaveComponentProps> = ({
    saveButton,
    cancelButton,
    deleteButton,
    disableAll,
    errorMessage,
    shouldBlockNavigation,
    withMargin = true,
}) => {
    const classes = useStyles()
    const dispatch = useAppDispatch()

    const buttons = getSaveComponentButtons({ saveButton, cancelButton, deleteButton })

    const buttonProps = useMemo<Record<SaveComponentButtonType, SaveComponentButtonItemProps>>(
        () => ({
            SAVE: {
                classname: clsx(
                    classes.SaveComponentButtonTypeSave,
                    errorMessage && classes.SaveComponentButtonTypeSaveError,
                ),
                icon: <SaveRoundedIcon />,
                dataCy: 'save',
            },
            CANCEL: {
                classname: classes.SaveComponentButtonTypeCancel,
                dataCy: 'cancel',
            },
            DELETE: {
                classname: classes.SaveComponentButtonTypeDelete,
                icon: <DeleteOutlineRoundedIcon />,
                dataCy: 'delete',
            },
        }),
        [
            classes.SaveComponentButtonTypeCancel,
            classes.SaveComponentButtonTypeDelete,
            classes.SaveComponentButtonTypeSave,
            classes.SaveComponentButtonTypeSaveError,
            errorMessage,
        ],
    )

    const onBlockedNavigation = () => {
        if (shouldBlockNavigation) {
            dispatch(
                displaySnackbar(
                    'warning',
                    'Vous avez effectué des modifications que vous n\'avez pas encore enregistrées. Cliquez sur le bouton "Enregistrer" ou "Annuler" du formulaire afin de poursuivre votre navigation.',
                ),
            )
            return false
        }
        return true
    }

    return (
        <Stack alignItems="center" gap="2rem" width="100%" className={clsx(withMargin && classes.SaveComponent)}>
            {errorMessage && (
                <div className={classes.SaveComponentErrorMessageContainer}>
                    <ErrorRoundedIcon className={classes.SaveComponentErrorMessageIcon} />
                    <Typography className={classes.SaveComponentErrorMessage}>{errorMessage}</Typography>
                </div>
            )}
            <div className={classes.SaveComponentButtons}>
                {buttons
                    .filter((button) => button.displayed)
                    .map((button) => {
                        const { classname, icon, dataCy } = buttonProps[button.type]

                        return (
                            <Button
                                key={button.type}
                                disableElevation
                                variant="contained"
                                color="inherit"
                                className={clsx(classes.SaveComponentButton, classname)}
                                startIcon={icon}
                                onClick={() => button.action()}
                                data-cy={dataCy}
                                disabled={disableAll || button.isLoading || button.disabled}
                            >
                                {button.label}
                            </Button>
                        )
                    })}
            </div>
            <Prompt when={!!shouldBlockNavigation} message={onBlockedNavigation} />
        </Stack>
    )
}

export default SaveComponent
